import { type ChangeEvent, type DragEvent, type FC, useState } from 'react';

import UploadIcon from '@/assets/svg/ui-elements/upload-outline.svg';
import { formatFileSize } from '@/utils/format-file-size';

import {
  StyledErrorMessage,
  StyledHiddenInput,
  StyledHint,
  StyledIconWrapper,
  StyledPrompt,
  StyledRoot,
  StyledTextContainer,
} from './Dropzone.styled';
import type { DropzoneProps } from './types';

const Dropzone: FC<DropzoneProps> = ({
  onFilesSelected,
  accept,
  multiple = false,
  disabled = false,
  acceptLabel,
  maxSize,
  subject = 'file',
  hint,
  icon,
  state = 'default',
  errorMessage,
  inputLabel,
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const isError = state === 'destructive';

  const hintContent =
    hint ??
    [acceptLabel, maxSize ? `up to ${formatFileSize(maxSize)}` : null].filter(Boolean).join(' · ');

  const selectFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || disabled) return;
    onFilesSelected(Array.from(fileList));
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
    selectFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (!disabled) setIsDraggingOver(true);
  };

  const handleDragLeave = () => setIsDraggingOver(false);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFiles(event.target.files);
    // Reset so selecting the same file again still fires onChange.
    event.target.value = '';
  };

  return (
    <StyledRoot
      $isDraggingOver={isDraggingOver}
      $disabled={disabled}
      $isError={isError}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <StyledIconWrapper>{icon ?? <UploadIcon />}</StyledIconWrapper>
      <StyledTextContainer>
        {isError && errorMessage ? (
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        ) : (
          <>
            <StyledPrompt>Drag and drop your {subject} here, or browse</StyledPrompt>
            {hintContent && <StyledHint>{hintContent}</StyledHint>}
          </>
        )}
      </StyledTextContainer>
      <StyledHiddenInput
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-invalid={isError || undefined}
        aria-label={inputLabel}
        onChange={handleInputChange}
      />
    </StyledRoot>
  );
};

Dropzone.displayName = 'Dropzone';

export default Dropzone;
